/**
 * ReportExport — client-side PDF/Excel export (FR-C3-15).
 * Renders entirely in the browser from PortalReportDetail DTO.
 * No server endpoint — uses jspdf + xlsx libraries.
 */
import { useState } from 'react';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import type { PortalReportDetail } from '@client/lib/queries/use-portal';

interface ReportExportProps {
  report: PortalReportDetail | null | undefined;
  studentName: string;
}

const RATING_LABEL: Record<string, string> = {
  excellent: 'Xuất sắc',
  good: 'Tốt',
  average: 'Khá',
  needs_improvement: 'Cần cải thiện',
};

const GRADE_TYPE_LABEL: Record<string, string> = {
  quiz: 'Kiểm tra', midterm: 'Giữa kỳ', final: 'Cuối kỳ',
  participation: 'Tham gia', assignment: 'Bài tập',
};

export function ReportExport({ report, studentName }: ReportExportProps) {
  const [exporting, setExporting] = useState(false);

  async function exportPdf() {
    if (!report) return;
    setExporting(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const r = report.report;
      let y = 20;

      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('BAO CAO TIEN DO HOC TAP', 105, y, { align: 'center' });
      y += 8;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Hoc vien: ${studentName}`, 20, y); y += 6;
      doc.text(`Lop: ${r.className} - ${r.courseName}`, 20, y); y += 6;
      doc.text(`Ky: ${r.periodLabel ?? r.period}`, 20, y); y += 6;
      if (r.periodStart && r.periodEnd) {
        doc.text(`Tu ${r.periodStart} den ${r.periodEnd}`, 20, y); y += 6;
      }
      if (r.overallRating) {
        doc.text(`Xep loai: ${RATING_LABEL[r.overallRating] ?? r.overallRating}`, 20, y); y += 6;
      }
      y += 4;

      // Sections
      const sections = [
        { title: 'Chuyen can', content: r.attendanceSummary },
        { title: 'Nhan xet hoc luc', content: r.academicComment },
        { title: 'Nhan xet hanh vi', content: r.behaviorComment },
        { title: 'Khuyen nghi', content: r.recommendation },
      ];

      for (const s of sections) {
        if (!s.content) continue;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(s.title, 20, y); y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(s.content, 170) as string[];
        doc.text(lines, 20, y);
        y += lines.length * 5 + 4;
        if (y > 260) { doc.addPage(); y = 20; }
      }

      // Grades trend
      if (report.trend.grades.length > 0) {
        y += 2;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text('Bang diem', 20, y); y += 6;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        for (const g of report.trend.grades) {
          const line = `${GRADE_TYPE_LABEL[g.label] ?? g.label}: ${g.score}/${g.maxScore} (${Math.round((g.score / g.maxScore) * 100)}%)`;
          doc.text(line, 24, y); y += 5;
          if (y > 270) { doc.addPage(); y = 20; }
        }
      }

      doc.save(`bao-cao-${studentName.replace(/\s+/g, '-')}-${r.periodLabel ?? r.period}.pdf`);
      toast.success('Đã xuất PDF');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Xuất PDF thất bại');
    } finally {
      setExporting(false);
    }
  }

  async function exportExcel() {
    if (!report) return;
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const r = report.report;

      // Sheet 1: report info
      const infoData = [
        ['Học viên', studentName],
        ['Lớp', r.className],
        ['Khóa học', r.courseName],
        ['Kỳ', r.periodLabel ?? r.period],
        ['Từ ngày', r.periodStart ?? ''],
        ['Đến ngày', r.periodEnd ?? ''],
        ['Xếp loại', r.overallRating ? (RATING_LABEL[r.overallRating] ?? r.overallRating) : ''],
        [''],
        ['Chuyên cần', r.attendanceSummary ?? ''],
        ['Nhận xét học lực', r.academicComment ?? ''],
        ['Nhận xét hành vi', r.behaviorComment ?? ''],
        ['Khuyến nghị', r.recommendation ?? ''],
      ];

      // Sheet 2: grades
      const gradeHeaders = ['Loại điểm', 'Điểm', 'Thang điểm', '%', 'Ngày chấm'];
      const gradeData = report.trend.grades.map((g) => [
        GRADE_TYPE_LABEL[g.label] ?? g.label,
        g.score,
        g.maxScore,
        `${Math.round((g.score / g.maxScore) * 100)}%`,
        g.gradedAt ? new Date(g.gradedAt).toLocaleDateString('vi-VN') : '',
      ]);

      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.aoa_to_sheet(infoData);
      const ws2 = XLSX.utils.aoa_to_sheet([gradeHeaders, ...gradeData]);

      XLSX.utils.book_append_sheet(wb, ws1, 'Báo cáo');
      XLSX.utils.book_append_sheet(wb, ws2, 'Bảng điểm');

      XLSX.writeFile(wb, `bao-cao-${studentName.replace(/\s+/g, '-')}-${r.periodLabel ?? r.period}.xlsx`);
      toast.success('Đã xuất Excel');
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error('Xuất Excel thất bại');
    } finally {
      setExporting(false);
    }
  }

  const disabled = !report || exporting;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="min-h-[44px] gap-2"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Đang xuất...' : 'Xuất báo cáo'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => void exportPdf()}
          disabled={disabled}
          className="gap-2 cursor-pointer"
        >
          <FileText className="h-4 w-4 text-destructive" />
          Xuất PDF
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => void exportExcel()}
          disabled={disabled}
          className="gap-2 cursor-pointer"
        >
          <FileSpreadsheet className="h-4 w-4 text-brand-green-600 dark:text-brand-green-400" />
          Xuất Excel
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
